/** Chi nhánh có liên kết dự án (API thường trả duAnId và/hoặc nested duAn). */
export interface IChiNhanhCoDuAn {
  duAnId?: number | null
  duAn?: { id?: number; ma?: string; ten?: string } | null
  ma?: string
  ten?: string
}

function getDuAnId(item: IChiNhanhCoDuAn): number {
  const id = item.duAnId ?? item.duAn?.id
  return id != null && Number.isFinite(Number(id)) ? Number(id) : 0
}

function getBranchSortLabel(item: IChiNhanhCoDuAn): string {
  return String(item.ten ?? item.ma ?? '')
}

/**
 * Sắp xếp chi nhánh theo dự án: `duAnId` (hoặc `duAn.id`) tăng dần,
 * trong cùng một dự án sắp theo tên/mã chi nhánh (locale tiếng Việt).
 * Không mutate mảng gốc.
 */
export function sortChiNhanhTheoDuAn<T extends IChiNhanhCoDuAn>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => {
    const dA = getDuAnId(a)
    const dB = getDuAnId(b)
    if (dA !== dB) return dA - dB
    return getBranchSortLabel(a).localeCompare(getBranchSortLabel(b), 'vi', { sensitivity: 'base' })
  })
}

/** Hai dòng hiển thị: dự án (mã/ tên) và chi nhánh (tên/ mã). */
export const getChiNhanhDisplayLines = (item: unknown): { line1: string; line2: string } => {
  const row = item as Record<string, unknown> | null | undefined
  const branch = (row?.chiNhanh ?? row) as Record<string, unknown> | undefined
  const duAn = (row?.duAn ?? branch?.duAn ?? row?.project) as Record<string, unknown> | undefined
  const duAnMa = String(duAn?.ma ?? row?.duAnMa ?? '').trim()
  const duAnTen = String(duAn?.ten ?? '').trim()
  const line1 = duAnMa || duAnTen
  const branchTen = String(branch?.ten ?? row?.ten ?? row?.chiNhanhTen ?? '').trim()
  const branchMa = String(branch?.ma ?? row?.ma ?? '').trim()
  const line2 = branchTen || branchMa
  return { line1, line2 }
}

/** Chuỗi tìm kiếm / tag Select (gộp hai dòng). */
export const getChiNhanhSearchLabel = (lines: { line1: string; line2: string }): string =>
  [lines.line1, lines.line2].filter(Boolean).join(' - ')

/** Nhãn ô lưới (không lặp tên dự án — chỉ mã/tên chi nhánh). */
export const getChiNhanhBranchCellLines = (item: unknown): { line1: string; line2: string } => {
  const row = item as Record<string, unknown> | null | undefined
  const branch = (row?.chiNhanh ?? row) as Record<string, unknown> | undefined
  const branchMa = String(branch?.ma ?? row?.ma ?? '').trim()
  const branchTen = String(branch?.ten ?? row?.ten ?? row?.chiNhanhTen ?? '').trim()
  if (branchMa && branchTen && branchMa !== branchTen) {
    return { line1: branchMa, line2: branchTen }
  }
  return { line1: '', line2: branchTen || branchMa }
}

export type ChiNhanhPickerGroup = {
  duAnId: number
  duAnLabel: string
  branches: Array<{
    key: string | number
    item: unknown
    cellLines: { line1: string; line2: string }
  }>
}

/** Nhóm chi nhánh theo dự án để lưới tối đa 2 hàng / nhóm. */
export const groupBranchesByDuAnForPicker = (items: readonly unknown[]): ChiNhanhPickerGroup[] => {
  const groups: ChiNhanhPickerGroup[] = []
  const indexByDuAn = new Map<number, number>()

  items.forEach((item, index) => {
    const row = item as Record<string, unknown>
    const branch = (row?.chiNhanh ?? row) as Record<string, unknown> | undefined
    const duAn = (row?.duAn ?? branch?.duAn) as Record<string, unknown> | undefined
    const duAnIdRaw = duAn?.id ?? row?.duAnId
    const duAnId = duAnIdRaw != null && Number.isFinite(Number(duAnIdRaw)) ? Number(duAnIdRaw) : 0
    const display = getChiNhanhDisplayLines(item)
    const branchEntry = {
      key: (row?.key ?? row?.id ?? index) as string | number,
      item,
      cellLines: getChiNhanhBranchCellLines(item),
    }

    const groupIndex = indexByDuAn.get(duAnId)
    if (groupIndex !== undefined) {
      groups[groupIndex].branches.push(branchEntry)
      return
    }
    indexByDuAn.set(duAnId, groups.length)
    groups.push({
      duAnId,
      duAnLabel: display.line1,
      branches: [branchEntry],
    })
  })

  return groups
}
